import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';

// Context for backward compatibility
interface AuthContextType {
  authState: {
    loading: boolean;
    loggedIn: boolean;
    user: any;
  };
  login: (user: any) => void;
  logout: () => void;
  fetchUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// AuthProvider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Get state and actions from store
  const { user, isLoggedIn, isLoading, isInitialized } = useAuthState();
  const { login, logout, fetchUser, initialize } = useAuthActions();

  // Initialize auth on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Handle redirects based on auth state - only after initialization is complete
  useEffect(() => {
    if (isInitialized && !isLoading) {
      const currentPath = location.pathname;
      const isLoginPage = currentPath === '/login';
      const isAuthLoginPage = currentPath === '/auth/login';
      const isRegisterPage = currentPath === '/register';
      const isLogoutPage = currentPath === '/logout';
      const isPublicPage =
        isLoginPage || isAuthLoginPage || isRegisterPage || isLogoutPage;

      // Only redirect if not already on the correct page to avoid loops
      if (!isLoggedIn && !isPublicPage) {
        navigate('/login', { replace: true });
      } else if (isLoggedIn && isPublicPage && !isLogoutPage) {
        // Redirect authenticated users away from login/register pages (but allow logout)
        navigate('/', { replace: true });
      }
    }
  }, [isLoggedIn, isLoading, isInitialized, location.pathname, navigate]);

  // Context value for backward compatibility
  const contextValue = useMemo(
    () => ({
      authState: {
        loading: isLoading,
        loggedIn: isLoggedIn,
        user,
      },
      login,
      logout,
      fetchUser,
    }),
    [isLoading, isLoggedIn, user, login, logout, fetchUser]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

// Backward compatibility hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Direct store hooks for optimal performance
export const useAuthUser = () => useAuthStore(state => state.user);
export const useAuthLoading = () => useAuthStore(state => state.isLoading);
export const useAuthError = () => useAuthStore(state => state.error);
export const useAuthLoggedIn = () => useAuthStore(state => state.isLoggedIn);
export const useAuthInitialized = () =>
  useAuthStore(state => state.isInitialized);

// User data hooks
export const useUserProducts = () => useAuthStore(state => state.userProducts);
export const useUserCourses = () => useAuthStore(state => state.userCourses);
export const useUserInfo = () => useAuthStore(state => state.userInfo);
// These hooks are removed as the properties don't exist in the store

// Permission and role hooks
export const useHasPermission = (permission: string) => {
  const user = useAuthUser();
  return useMemo(() => {
    return user?.permissions?.includes(permission) || false;
  }, [user?.permissions, permission]);
};

export const useHasRole = (role: string) => {
  const user = useAuthUser();
  return useMemo(() => {
    return user?.roles?.includes(role) || false;
  }, [user?.roles, role]);
};

export const useIsAuthenticated = () => {
  const user = useAuthUser();
  const isLoggedIn = useAuthLoggedIn();
  return useMemo(() => {
    return isLoggedIn && !!user;
  }, [isLoggedIn, user]);
};

// Actions hook
export const useAuthActions = () => {
  const login = useAuthStore(state => state.login);
  const logout = useAuthStore(state => state.logout);
  const updateUser = useAuthStore(state => state.updateUser);
  const fetchUser = useAuthStore(state => state.fetchUser);
  const initialize = useAuthStore(state => state.initialize);
  const clearError = useAuthStore(state => state.clearError);
  const fetchUserProducts = useAuthStore(state => state.fetchUserProducts);
  const fetchUserCourses = useAuthStore(state => state.fetchUserCourses);
  const fetchUserInfo = useAuthStore(state => state.fetchUserInfo);
  const clearUserData = useAuthStore(state => state.clearUserData);

  return {
    login,
    logout,
    updateUser,
    fetchUser,
    initialize,
    clearError,
    fetchUserProducts,
    fetchUserCourses,
    fetchUserInfo,
    clearUserData,
  };
};

// State hook
export const useAuthState = () => {
  const user = useAuthStore(state => state.user);
  const isLoading = useAuthStore(state => state.isLoading);
  const error = useAuthStore(state => state.error);
  const isLoggedIn = useAuthStore(state => state.isLoggedIn);
  const isInitialized = useAuthStore(state => state.isInitialized);

  return {
    user,
    isLoading,
    error,
    isLoggedIn,
    isInitialized,
  };
};
